// src/modals/ClientContactsModal.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const ClientContactsModal = ({ 
  open, 
  onClose, 
  contacts = [], 
  clientName, 
  primaryContact 
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          color: 'text.primary'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
          {clientName} - All Contacts
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Complete contact directory for {clientName}.
          </Typography>
          
          <Typography variant="body2" color="text.secondary">
            This modal will display all contacts associated with this client,
            including primary contacts, project managers, and key stakeholders.
          </Typography>
          
          {contacts.length > 0 && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Found {contacts.length} contacts for this client.
            </Typography>
          )}
          
          {primaryContact && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(3, 218, 198, 0.1)', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', mb: 1 }}>
                Primary Contact
              </Typography>
              <Typography variant="body2">
                {primaryContact.name || primaryContact.full_name}
              </Typography>
              {primaryContact.title && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {primaryContact.title}
                </Typography>
              )}
              {primaryContact.email && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {primaryContact.email}
                </Typography>
              )}
              {primaryContact.phone && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {primaryContact.phone}
                </Typography>
              )}
            </Box>
          )}
          
          {contacts && contacts.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 'bold', mb: 1 }}>
                Additional Contacts
              </Typography>
              {contacts.map((contact, index) => (
                <Box key={index} sx={{ mt: 1, p: 2, bgcolor: 'rgba(0, 0, 0, 0.05)', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {contact.name || contact.full_name}
                  </Typography>
                  {contact.title && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {contact.title}
                    </Typography>
                  )}
                  {contact.email && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {contact.email}
                    </Typography>
                  )}
                  {contact.phone && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {contact.phone}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ClientContactsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contacts: PropTypes.array,
  clientName: PropTypes.string,
  primaryContact: PropTypes.object
};

export default ClientContactsModal;
